/**
 * Pragmatic split editor for the "Run Code" action of the Code nodetype.
 *
 * Renders TWO editors side-by-side:
 *  - A code-friendly textarea for the `code` field (Python source, multi-line)
 *  - The standard JsonViewEdit for everything else (timeout, references, ...)
 *
 * Backend convention (sympy bypass + multi-line) is hidden from the user:
 * we wrap the typed Python in single-quotes, escape `"`, `'`, `\\`, and
 * convert newlines to `\n` escape sequences when saving. On load we reverse
 * the same transformation so the textarea shows raw Python.
 *
 * NOTE: This is a hardcoded MVP for the Code nodetype. Long-term the right
 * answer is per-field UI driven by backend metadata (Stage 2-5 in the
 * symmy-code design doc); this is the Stage 1 stopgap so the UX isn't
 * "edit raw JSON with sympy escape rules in your head".
 */

import { memo, useEffect, useState, ChangeEvent, KeyboardEvent } from 'react';
import { Button, Input, Modal, Space, Typography } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';

import { JsonViewEdit } from '@components';

import styles from './NodeConfiguration.module.scss';
import { NodeData } from '../../../types/node';

type Props = {
  data: NodeData;
  setNodeInput: (value: Record<string, unknown>) => void;
};

/**
 * Convert raw Python source (as typed in the textarea) into the wire form
 * expected by the Symmy flow engine: single-quoted string with escape
 * sequences. The flow engine's `evaluate()` (sympy-based) treats this as a
 * Python string literal and returns its content verbatim.
 */
const wrapCode = (rawPython: string): string => {
  if (rawPython === '') return '';
  const escaped = rawPython
    .replace(/\\/g, '\\\\')   // backslash first — order matters
    .replace(/'/g, "\\'")
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t');
  return `'${escaped}'`;
};

/**
 * Reverse of wrapCode: takes the stored single-quoted form and returns the
 * raw Python source for display in the textarea.
 */
const unwrapCode = (stored: unknown): string => {
  if (typeof stored !== 'string') return '';
  let s = stored;
  if (s.length >= 2 && s.startsWith("'") && s.endsWith("'")) {
    s = s.slice(1, -1);
  }
  // Reverse the escapes — order matters (backslash last)
  return s
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t')
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, '\\');
};

const CodeNodeInputEditor = ({ data, setNodeInput }: Props) => {
  const { _ } = useLingui();
  const { Text } = Typography;

  const initial = (data.input || {}) as Record<string, unknown>;
  const { code: initialCodeWrapped, ...initialOther } = initial;

  const [codeValue, setCodeValue] = useState<string>(
    unwrapCode(initialCodeWrapped),
  );
  const [otherInputs, setOtherInputs] = useState<Record<string, unknown>>(
    initialOther,
  );
  const [isCodeFullscreen, setIsCodeFullscreen] = useState(false);

  // Re-sync local state if the underlying node.input changes externally
  // (e.g. backend refetch, undo/redo).
  useEffect(() => {
    const next = (data.input || {}) as Record<string, unknown>;
    const { code: nextCodeWrapped, ...nextOther } = next;
    setCodeValue(unwrapCode(nextCodeWrapped));
    setOtherInputs(nextOther);
  }, [data.input]);

  const handleCodeChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newCode = e.target.value;
    setCodeValue(newCode);
    setNodeInput({ ...otherInputs, code: wrapCode(newCode) });
  };

  const handleOtherChange = (newOther: Record<string, unknown>) => {
    setOtherInputs(newOther);
    setNodeInput({ ...newOther, code: wrapCode(codeValue) });
  };

  /**
   * Browser-default Tab moves focus out of the textarea, which is hostile
   * for code editing. Capture Tab and insert 4 spaces; Shift+Tab removes up
   * to 4 spaces from the start of the current line.
   *
   * Selection-spanning multi-line indent (Tab/Shift+Tab applied per
   * selected line) isn't implemented here — keeps the MVP simple. If we
   * later want it, the standard pattern is to find every line touched by
   * the selection and prepend / strip 4 spaces on each.
   */
  const handleCodeKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();

    const target = e.currentTarget;
    const { selectionStart, selectionEnd, value } = target;
    const INDENT = '    '; // 4 spaces — Python convention

    if (e.shiftKey) {
      // De-indent: remove up to 4 leading spaces from the line containing
      // the cursor.
      const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
      const lineHead = value.slice(lineStart, lineStart + INDENT.length);
      const stripMatch = lineHead.match(/^ {1,4}/);
      if (!stripMatch) return;

      const stripLen = stripMatch[0].length;
      const next =
        value.slice(0, lineStart) + value.slice(lineStart + stripLen);
      const cursor = Math.max(selectionStart - stripLen, lineStart);

      setCodeValue(next);
      setNodeInput({ ...otherInputs, code: wrapCode(next) });
      // Cursor restore happens after React re-renders — RAF is enough.
      requestAnimationFrame(() => target.setSelectionRange(cursor, cursor));
    } else {
      // Insert 4 spaces at the cursor (replaces any selection).
      const next =
        value.slice(0, selectionStart) + INDENT + value.slice(selectionEnd);
      const cursor = selectionStart + INDENT.length;

      setCodeValue(next);
      setNodeInput({ ...otherInputs, code: wrapCode(next) });
      requestAnimationFrame(() => target.setSelectionRange(cursor, cursor));
    }
  };

  return (
    <>
      <div className={styles.configuredItem}>
        <div className={styles.codeHeader}>
          <Text className={styles.configuredLabel}>
            <Trans>Python code</Trans>
          </Text>
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<ExpandOutlined />}
              onClick={() => setIsCodeFullscreen(true)}
            >
              <Trans>Expand</Trans>
            </Button>
          </Space>
        </div>
        <Input.TextArea
          value={codeValue}
          onChange={handleCodeChange}
          onKeyDown={handleCodeKeyDown}
          placeholder={_(msg`output = data['product_a'][0]['name']`)}
          autoSize={{ minRows: 10, maxRows: 30 }}
          className={styles.codeTextarea}
          spellCheck={false}
        />
      </div>

      <div className={styles.configuredItem}>
        <Text className={styles.configuredLabel}>
          <Trans>Other inputs (JSON)</Trans>
        </Text>
        <JsonViewEdit
          value={otherInputs}
          onChange={handleOtherChange}
          placeholder={_(msg`Enter JSON input data (references, timeout, ...) ...`)}
          minRows={5}
          maxRows={15}
        />
      </div>

      {/* Fullscreen modal for the code textarea — same value & onChange,
        * so edits in the modal flow back to the inline editor (and vice
        * versa) without manual sync. */}
      <Modal
        title={<Trans>Python code</Trans>}
        open={isCodeFullscreen}
        onCancel={() => setIsCodeFullscreen(false)}
        footer={null}
        width="80%"
        centered
        destroyOnClose
      >
        <Input.TextArea
          value={codeValue}
          onChange={handleCodeChange}
          onKeyDown={handleCodeKeyDown}
          placeholder={_(msg`output = data['product_a'][0]['name']`)}
          // Fixed-height fill: ~70vh keeps it large but leaves room for
          // window chrome / modal padding.
          className={`${styles.codeTextarea} ${styles.codeTextareaFullscreen}`}
          spellCheck={false}
          autoFocus
        />
      </Modal>
    </>
  );
};

export default memo(CodeNodeInputEditor);
